import React, { useState, useRef, useEffect, useMemo } from "react";
import { resolvePublicUploadUrl } from "../../../api/apiConfig/apiConfig";
import { getWalletPageLabel } from "../../../utils/walletTypeLabels";
import {
  filterTransferCurrencies,
  getTransferAmountError,
  isUsdtOnlyTransferInvolved,
} from "./walletTransferHelpers";
import "./walletTransferModal.css";

/** Resolve display label: prefer API-provided label, fall back to computed label. */
function resolveWalletLabel(type, labelsMap) {
  if (labelsMap && labelsMap[type]) return labelsMap[type];
  return getWalletPageLabel(type);
}

function coinIconSrc(icon_path) {
  if (!icon_path) return "/images/tether_icon.png";
  return resolvePublicUploadUrl(icon_path);
}

export default function WalletTransferModals({
  fromWalletType,
  setFromWalletType,
  toWalletType,
  setToWalletType,
  availableWalletTypes,
  walletLabelsMap,
  selectedCurrency,
  setSelectedCurrency,
  transferAmount,
  setTransferAmount,
  transferable = "0",
  balanceLoading = false,
  sourceHint,
  handleInterchangeWallet,
  handleTransfer,
  currencyData,
  fiveDecimal,
  marginInvolved,
  marginPairs,
  selectedMarginPair,
  setSelectedMarginPair,
  marginAssetType,
  setMarginAssetType,
  marginPairDropOpen,
  setMarginPairDropOpen,
}) {
  const [coinDropOpen, setCoinDropOpen] = useState(false);
  const [coinSearch, setCoinSearch] = useState("");
  const coinDropRef = useRef(null);

  useEffect(() => {
    if (!coinDropOpen) return;
    function handleClick(e) {
      if (coinDropRef.current && !coinDropRef.current.contains(e.target)) {
        setCoinDropOpen(false);
        setCoinSearch("");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [coinDropOpen]);

  const [fromDropOpen, setFromDropOpen] = useState(false);
  const [toDropOpen, setToDropOpen] = useState(false);
  const fromDropRef = useRef(null);
  const toDropRef = useRef(null);

  useEffect(() => {
    if (!fromDropOpen) return;
    function handleClick(e) {
      if (fromDropRef.current && !fromDropRef.current.contains(e.target)) {
        setFromDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [fromDropOpen]);

  useEffect(() => {
    if (!toDropOpen) return;
    function handleClick(e) {
      if (toDropRef.current && !toDropRef.current.contains(e.target)) {
        setToDropOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [toDropOpen]);

  const maxAmount = parseFloat(transferable || 0);
  const amountError = getTransferAmountError(transferAmount, transferable, { balanceLoading });
  const amountValid =
    !balanceLoading &&
    transferAmount &&
    Number(transferAmount) > 0 &&
    !amountError;

  const usdtOnlyInvolved = isUsdtOnlyTransferInvolved(fromWalletType, toWalletType);
  const pickerCurrencies = useMemo(
    () => filterTransferCurrencies(currencyData, fromWalletType, toWalletType),
    [currencyData, fromWalletType, toWalletType]
  );

  const selectedCoin =
    marginInvolved && selectedMarginPair
      ? marginAssetType === "base"
        ? selectedMarginPair.base_asset
        : selectedMarginPair.quote_asset
      : usdtOnlyInvolved
        ? "USDT"
        : selectedCurrency?.short_name || selectedCurrency?.currency || "";

  const filteredCoins = pickerCurrencies.filter((item) => {
    if (!coinSearch) return true;
    const s = coinSearch.toLowerCase();
    return (
      (item?.short_name || "").toLowerCase().includes(s) ||
      (item?.currency || "").toLowerCase().includes(s)
    );
  });

  const usdtOnly = usdtOnlyInvolved;
  const usdtCoin = filteredCoins[0] || null;

  const coinPicker = usdtOnly ? (
    <div className="crypto_selectcoin">
      <h6>Crypto</h6>
      <div className="coin_cryptofiled">
        <button type="button" disabled style={{ cursor: "default", opacity: 1 }}>
          <span>
            <img
              src={coinIconSrc(usdtCoin?.icon_path)}
              alt="USDT"
              width="20px"
              onError={(e) => { e.target.src = "/images/tether_icon.png"; }}
            />
            USDT
          </span>
        </button>
      </div>
    </div>
  ) : (
    <div className="crypto_selectcoin">
      <h6>Crypto</h6>
      <div className="coin_cryptofiled" style={{ position: "relative" }} ref={coinDropRef}>
        <button
          type="button"
          onClick={() => { setCoinDropOpen((o) => !o); setCoinSearch(""); }}
        >
          {Object.keys(selectedCurrency || {}).length > 0 ? (
            <span>
              <img
                src={coinIconSrc(selectedCurrency?.icon_path)}
                alt={selectedCurrency?.short_name || "crypto"}
                width="20px"
                onError={(e) => { e.target.src = "/images/AnnouncementImg/activities_icon.png"; }}
              />
              {selectedCurrency?.short_name || selectedCurrency?.currency || "Crypto"}
            </span>
          ) : (
            <span>
              <img src="/images/tether_icon.png" alt="Crypto" />
              Crypto
            </span>
          )}
          <i className="ri-arrow-down-s-fill" />
        </button>

        {coinDropOpen && (
          <div className="margin-pair-dropdown" style={{ maxHeight: 260 }}>
            <div style={{ padding: "8px 10px 4px" }}>
              <input
                type="text"
                placeholder="Search coin"
                value={coinSearch}
                onChange={(e) => setCoinSearch(e.target.value)}
                className="wallet-transfer-search-input"
                autoFocus
              />
            </div>
            {filteredCoins.length > 0 ? (
              filteredCoins.map((item, idx) => (
                <div
                  key={item?.currency_id || idx}
                  className={`margin-pair-option${selectedCurrency?.currency_id === item?.currency_id ? " margin-pair-option--active" : ""}`}
                  onClick={() => {
                    setSelectedCurrency(item);
                    setCoinDropOpen(false);
                    setCoinSearch("");
                  }}
                >
                  <img
                    src={coinIconSrc(item?.icon_path)}
                    alt={item?.short_name || "coin"}
                    width="20px"
                    onError={(e) => { e.target.src = "/images/AnnouncementImg/activities_icon.png"; }}
                  />
                  {item?.short_name}
                  {item?.currency && item.currency !== item.short_name && (
                    <span style={{ marginLeft: "auto", fontSize: 11, color: "#94a3b8" }}>{item.currency}</span>
                  )}
                </div>
              ))
            ) : (
              <div style={{ padding: "12px 14px", fontSize: 13, color: "#94a3b8" }}>No coins found</div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const amountField = (
    <div className="crypto_selectcoin">
      <h6>Amount</h6>
      <div className={`price_max_total${amountError ? " wallet-transfer-amount--error" : ""}`}>
        <input
          type="number"
          placeholder="0"
          value={transferAmount}
          onChange={(e) => setTransferAmount(e.target.value)}
          onWheel={(e) => e.target.blur()}
          aria-invalid={amountError ? "true" : undefined}
          aria-describedby={amountError ? "wallet-transfer-amount-error" : undefined}
        />
        <div className="d-flex gap-0">
          {selectedCoin || selectedCurrency?.short_name || ""}
          <button
            type="button"
            disabled={balanceLoading}
            onClick={() => setTransferAmount(String(transferable || "0"))}
          >
            All
          </button>
        </div>
      </div>
      {amountError ? (
        <p id="wallet-transfer-amount-error" className="wallet-transfer-amount-error" role="alert">
          {amountError}
        </p>
      ) : null}
    </div>
  );

  const availableLegend = (
    <legend>
      Available：
      <span>
        {balanceLoading ? (
          <span className="wallet-transfer-balance-skeleton" aria-hidden="true" />
        ) : (
          <>
            {maxAmount.toFixed(8)} {selectedCoin || selectedCurrency?.short_name || ""}
          </>
        )}
      </span>
    </legend>
  );

  const confirmActions = (
    <div className="margin-modal-actions" style={{ marginTop: 16 }}>
      <button type="button" className="btn margin-modal-btn-cancel" data-bs-dismiss="modal">Cancel</button>
      <button
        type="button"
        className="btn margin-modal-btn-confirm"
        disabled={!amountValid}
        onClick={handleTransfer}
      >
        Confirm
      </button>
    </div>
  );

  return (
    <div className="modal fade kyc_modal" id="walletTransferModal" tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Funds Transfer</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" />
          </div>

          <div className="modal-body">
            <div className="wallet_transfer">

              <div className="wallet_form">
                <div className="form_select">
                  <div className="currencyicon">
                    <img src="/images/dollaricon.svg" alt="dollaricon" />
                  </div>
                  <div className="formfiled">
                    <label>From</label>
                    <div className="select_spot" style={{ position: "relative" }} ref={fromDropRef}>
                      <button
                        type="button"
                        className="wallet-transfer-custom-select"
                        onClick={() => setFromDropOpen((o) => !o)}
                      >
                        <span>{resolveWalletLabel(fromWalletType, walletLabelsMap) || "N/A"}</span>
                        <i className="ri-arrow-down-s-line" />
                      </button>
                      {fromDropOpen && (
                        <div className="margin-pair-dropdown wallet-transfer-dropdown">
                          {availableWalletTypes?.length > 0 ? (
                            availableWalletTypes
                              .filter((w) => w !== toWalletType)
                              .map((wallet, idx) => (
                                <div
                                  key={idx}
                                  className={`margin-pair-option${fromWalletType === wallet ? " margin-pair-option--active" : ""}`}
                                  onClick={() => {
                                    setFromWalletType(wallet);
                                    setFromDropOpen(false);
                                  }}
                                >
                                  {resolveWalletLabel(wallet, walletLabelsMap)}
                                </div>
                              ))
                          ) : (
                            <div className="margin-pair-option text-muted">N/A</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form_select refreshcntr">
                  <div className="currencyicon">
                    <img src="/images/arrowbottom.svg" alt="arrowbottom" />
                  </div>
                  <div className="formfiled" />
                  <div className="currencyicon" style={{ cursor: "pointer" }} onClick={handleInterchangeWallet}>
                    <img src="/images/refreshicon.svg" alt="refreshicon" />
                  </div>
                </div>

                <div className="form_select">
                  <div className="currencyicon">
                    <img src="/images/dollaricon.svg" alt="dollaricon" />
                  </div>
                  <div className="formfiled">
                    <label>To</label>
                    <div className="select_spot" style={{ position: "relative" }} ref={toDropRef}>
                      <button
                        type="button"
                        className="wallet-transfer-custom-select"
                        onClick={() => setToDropOpen((o) => !o)}
                      >
                        <span>{resolveWalletLabel(toWalletType, walletLabelsMap) || "N/A"}</span>
                        <i className="ri-arrow-down-s-line" />
                      </button>
                      {toDropOpen && (
                        <div className="margin-pair-dropdown wallet-transfer-dropdown">
                          {availableWalletTypes?.length > 0 ? (
                            availableWalletTypes
                              .filter((w) => w !== fromWalletType)
                              .map((wallet, idx) => (
                                <div
                                  key={idx}
                                  className={`margin-pair-option${toWalletType === wallet ? " margin-pair-option--active" : ""}`}
                                  onClick={() => {
                                    setToWalletType(wallet);
                                    setToDropOpen(false);
                                  }}
                                >
                                  {resolveWalletLabel(wallet, walletLabelsMap)}
                                </div>
                              ))
                          ) : (
                            <div className="margin-pair-option text-muted">N/A</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {marginInvolved ? (
                <>
                  <div className="crypto_selectcoin">
                    <h6>Pair</h6>
                    <div className="coin_cryptofiled" style={{ position: "relative" }}>
                      <button type="button" onClick={() => setMarginPairDropOpen((o) => !o)}>
                        <span>
                          <img
                            src={selectedMarginPair?.icon_path ? resolvePublicUploadUrl(selectedMarginPair.icon_path) : "/images/default_coin.png"}
                            alt={selectedMarginPair?.pair || "pair"}
                            width="20px"
                            onError={(e) => { e.target.src = "/images/AnnouncementImg/activities_icon.png"; }}
                          />
                          {selectedMarginPair
                            ? `${selectedMarginPair.base_asset}/${selectedMarginPair.quote_asset}`
                            : "Select Pair"}
                        </span>
                        <i className="ri-arrow-down-s-fill" />
                      </button>
                      {marginPairDropOpen && Array.isArray(marginPairs) && marginPairs.length > 0 && (
                        <div className="margin-pair-dropdown">
                          {marginPairs.map((p) => {
                            const label = `${p.base_asset}/${p.quote_asset}`;
                            const isActive = selectedMarginPair?.pair_id === p.pair_id;
                            return (
                              <div
                                key={p.pair_id}
                                className={`margin-pair-option${isActive ? " margin-pair-option--active" : ""}`}
                                onClick={() => {
                                  setSelectedMarginPair(p);
                                  setMarginAssetType("base");
                                  setMarginPairDropOpen(false);
                                }}
                              >
                                <img
                                  src={p.icon_path ? resolvePublicUploadUrl(p.icon_path) : "/images/default_coin.png"}
                                  alt={label}
                                  width="20px"
                                  onError={(e) => { e.target.src = "/images/AnnouncementImg/activities_icon.png"; }}
                                />
                                {label}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedMarginPair && (
                    <div className="margin-coin-radios">
                      {[
                        { label: selectedMarginPair.base_asset, value: "base" },
                        { label: selectedMarginPair.quote_asset, value: "quote" },
                      ].map(({ label, value }) => (
                        <label key={value} className="margin-coin-radio">
                          <input
                            type="radio"
                            name="margin-transfer-coin"
                            checked={marginAssetType === value}
                            onChange={() => setMarginAssetType(value)}
                          />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {amountField}
                  {availableLegend}
                  {sourceHint ? (
                    <p className="small text-muted mb-0" style={{ marginTop: 8 }}>{sourceHint}</p>
                  ) : null}
                  {confirmActions}
                </>
              ) : (
                <>
                  {coinPicker}
                  {amountField}
                  {availableLegend}
                  {sourceHint ? (
                    <p className="small text-muted mb-0" style={{ marginTop: 8 }}>{sourceHint}</p>
                  ) : null}
                  {confirmActions}
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
